import { Component, ElementRef, Input, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { Telematics } from 'src/app/modules/shared/models/telematics.model';
import { TranslateService } from '@ngx-translate/core';
import { VehicleService } from 'src/app/modules/shared/services/vehicle.service';
import { getCarImageAbsoluteURL, getCustomerDisplayName, setDefaultImg, unsubscribeSubscriptions } from 'src/app/modules/shared/services/util.service';
import { AppointmentService } from 'src/app/modules/shared/services/appointment.service';
import { AppointmentType, Department } from '@signal/asp-data-commons';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import { LuxonDateAdapter } from 'src/app/modules/shared/services/luxon-date-adapter';
import { CalendarHeaderComponent } from '../../../bdc/shared/calendar/calendar-header/calendar-header.component';
import { DateTime } from 'luxon';
import { MatCalendar } from '@angular/material/datepicker';
import { SubscriptionList } from 'src/app/modules/shared/models/asp.types';

@Component({
  selector: 'app-fleet-appointment',
  templateUrl: './fleet-appointment.component.html',
  styleUrls: ['./fleet-appointment.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: LuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }

  ],
  encapsulation: ViewEncapsulation.None
})
export class FleetAppointmentComponent implements OnInit, OnDestroy {
  @ViewChild('fleetcalendar', { static: false }) calendar: MatCalendar<DateTime>;
  @ViewChild('fleetcalendar', { read: ElementRef }) calendarRef: ElementRef;
  @Input('vehicle') vehicle: any;
  @Input('searchValues') searchValues;
  isTelematicsLoading = true;
  telematics: Telematics;
  setDefaultImg = setDefaultImg;
  customHeader = CalendarHeaderComponent;
  minDate: string;
  selectedDate;
  dateFormat = 'yyyy-MM-dd';
  filters = { status: 'Scheduled', appointmentType: AppointmentType.TEST_DRIVE, vin: '' };
  appointments: any[];
  selectedDateAppts: any;
  isMonthApptsLoading = true;
  subscriptions: SubscriptionList = {};

  constructor(private readonly vehicleService: VehicleService,
    private readonly appointmentService: AppointmentService,
    private readonly translate: TranslateService) {
    this.minDate = DateTime.now().toFormat(this.dateFormat);
  }

  ngOnInit(): void {
    this.initializeInfo();
  }

  initializeInfo() {
    this.isMonthApptsLoading = true;
    const endDate = DateTime.fromFormat(this.minDate, this.dateFormat).endOf('month').toFormat(this.dateFormat);
    this.getAppointmentDetails(this.minDate, endDate);
    this.isTelematicsLoading = true;
    this.fetchTelematics();
    this.selectedDateAppts = [];
    this.selectedDate = '';
  }

  ngOnChanges(changes: SimpleChanges) {
    const vehicle = changes.vehicle?.currentValue;
    if (!!vehicle && vehicle.vin !== changes.vehicle?.previousValue?.vin) {
      this.initializeInfo();
    }
  }

  private getAppointmentDetails(startDate: string, endDate?: string) {

    this.filters = { ...this.filters, vin: this.vehicle.vin };
    this.subscriptions.apptData = this.appointmentService.getAppointmentSummary(Department.SALES, 
      startDate, endDate, this.filters, '').subscribe(data => {
      this.appointments = data.data;
      this.isMonthApptsLoading= false;
      this.calendar?.updateTodaysDate();
    }, err => {
      this.appointments = [];
      this.isMonthApptsLoading = false;
      this.calendar?.updateTodaysDate();
    });

  }
  private fetchTelematics() {
    this.subscriptions.telematics = this.vehicleService.getTelematics(this.vehicle.vin,Department.SALES)
      .subscribe(data => {
        if (data.status.code === 200) {
          this.telematics = Telematics.getTelematics(data?.data);
        } else {
          this.telematics = Telematics.getDefaultTelematics();
        }
        this.isTelematicsLoading = false;
      }, err => {
        this.isTelematicsLoading = false;
        this.telematics = Telematics.getDefaultTelematics();
      });
  }

  getTelematicsTranslation(value) {
    if (value) {
      const translatedTxt = value?.split(':');
      return this.translate.instant(`${translatedTxt[0]}`) + (translatedTxt[1] === '(Not available)' ? `(${this.translate.instant(`testDrive.notAvailable`)})` : translatedTxt[1]);
    }
  }

  getImage() {
    return getCarImageAbsoluteURL(this.vehicle?.imageUrl);
  }

  getAppointmentCustomerDisplayName(customer: any): string {
    return getCustomerDisplayName(customer);
  }
  
  dateClass = (event: any) => {
    let classname = '';
    const date = event.toFormat(this.dateFormat);
    const apptCount = this.appointments?.find(x => x.date === date)?.count;
    if ( DateTime.fromISO(date) >= DateTime.fromISO(this.minDate) && apptCount > 0) {
      classname += 'appointmentExists';
        this.displayMonth(`${apptCount}`, date);
    }
    classname += this.selectedDate === date ? " selected" : " ";
    return classname;
  }

  displayMonth(count: string, date: string) {
    setTimeout(() => {
      const dayCellsSelector = this.calendarRef.nativeElement.querySelectorAll('.mat-calendar-body-cell');
      dayCellsSelector.forEach(y => {
        const div = y.querySelectorAll('div.mat-calendar-body-cell-content')[0];
        if (+div?.innerText === +DateTime.fromISO(date).toFormat('dd')) {
          y.setAttribute("aria-label", count);
          const span = document.createElement('span');
          span.innerHTML = count;
          span.classList.add('appt-count');
          div.appendChild(span);
        }
      })
    }, 250)
  }

  monthChange(event) {
    this.selectedDateAppts = [];
    this.selectedDate = '';
    const date = event.toFormat(this.dateFormat);
    const startDate = DateTime.fromFormat(date, this.dateFormat).startOf('month').toFormat(this.dateFormat);
    const endDate = DateTime.fromFormat(date, this.dateFormat).endOf('month').toFormat(this.dateFormat);
    this.getAppointmentDetails(startDate, endDate);
  }

  dateChange(event: any, calendar: any) {
    const date = event.toFormat(this.dateFormat);
    this.selectedDate = date;
    this.selectedDateAppts = this.appointments.filter(x => x.date === date);
    calendar.updateTodaysDate();
  } 

  getVehicleDescription() {
    return `${this.vehicle.year} ${this.vehicle?.marketingTitle ? this.vehicle.marketingTitle : this.vehicle.model}`;
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subscriptions);
  }

}
